/**
 * Resolves the virtualizer's row-slot count — distinct from `aria-rowcount`,
 * which U4's `deriveAriaRowCount` owns separately. In the sparse model the count
 * is the loaded rows plus a short placeholder LOOKAHEAD, growing as pages land —
 * DataGridPro's `onRowsScrollEnd` behavior, and what keeps a fast scroll from
 * showing a viewport-filling wall of placeholder rows (QA round 12). The spacer
 * is therefore loaded-bound; the ceiling guard below covers a pathological
 * loaded count the same way it covered `serverTotal`.
 */

/** Conservative single-element height cap (px): below the ~17.9M Firefox/Safari floor so the fallback engages before any browser clamps. */
export const MAX_SAFE_ELEMENT_HEIGHT_PX = 15_000_000

/**
 * Placeholder slots laid out BEYOND the loaded frontier while more pages exist.
 * Enough that reaching the bottom shows loading is happening (and keeps the fetch
 * sentinel scroll-reachable), few enough that a fast scroll never fills the
 * viewport with placeholders.
 */
export const PLACEHOLDER_LOOKAHEAD_ROWS = 5

export type ResolveVirtualCountParams = {
  /**
   * RAW loaded row count (the flat pre-dedup `serverOffsetCount`), NOT the deduped logical
   * count. A sparse slot index IS a server offset, so the floor/clamp below must cover EVERY
   * loaded offset — flooring on the deduped count would leave a loaded row at a high offset
   * (past a dropped cross-page duplicate) with no slot, unrenderable. `aria-rowcount` is the
   * one that floors on the deduped logical count; these are two different index spaces.
   */
  loadedCount: number
  /** Announced server total. `undefined` while unknown. */
  serverTotal?: number
  /** `dense` always returns `loadedCount`. */
  countModel: 'sparse' | 'dense'
  /** Per-row height in px for the active density. */
  rowHeightPx: number
  /** Ceiling override (testing). */
  maxElementHeightPx?: number
}

export type ResolvedVirtualCount = {
  count: number
  /**
   * True when the sparse spacer would have crossed the ceiling and the count fell
   * back to `loadedCount`. A consumer MAY surface this (e.g. keep a "load more"
   * affordance, since the scrollbar is no longer pre-sized to the full total).
   */
  clampedToLoaded: boolean
}

export const resolveVirtualCount = (params: ResolveVirtualCountParams): ResolvedVirtualCount => {
  const {
    loadedCount,
    serverTotal,
    countModel,
    rowHeightPx,
    maxElementHeightPx = MAX_SAFE_ELEMENT_HEIGHT_PX,
  } = params

  if (countModel === 'dense' || serverTotal === undefined) {
    return { count: loadedCount, clampedToLoaded: false }
  }

  // Loaded rows + a short placeholder lookahead while more pages remain; exactly the
  // loaded count once everything is in. Floored at `loadedCount` for an UNDER-reporting
  // `serverTotal` (estimated/search-style totals): fewer slots than loaded rows would
  // leave a loaded row unrenderable with no fetch to recover it (no-silent-failures).
  const count = Math.max(
    Math.min(serverTotal, loadedCount + PLACEHOLDER_LOOKAHEAD_ROWS),
    loadedCount,
  )

  // Guard the browser's single-element height cap. Loaded-bound counts make this
  // pathological in practice, but a huge loaded set still degrades linearly.
  const wouldExceedCeiling = count * rowHeightPx > maxElementHeightPx
  if (wouldExceedCeiling) {
    return { count: loadedCount, clampedToLoaded: true }
  }

  return { count, clampedToLoaded: false }
}
